import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import {
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
    resolveMsiSxRadius,
} from '../../../styles/msiStyleTokens';

function FactorDataStatusControl({
    summary,
    onLoadClick,
    loadDisabled = false,
    loadInProgress = false,
    detailsOpen,
    onDetailsOpenChange,
}) {
    const [internalDetailsOpen, setInternalDetailsOpen] = useState(false);
    const isDetailsControlled = typeof onDetailsOpenChange === 'function';
    const resolvedDetailsOpen = isDetailsControlled ? Boolean(detailsOpen) : internalDetailsOpen;
    const showLoadAction = Boolean(summary?.showLoadAction);
    const detailsTooltip = useMemo(() => {
        if (loadInProgress) return 'Loading factor data';
        if (summary?.loading) return 'Checking factor data';
        return showLoadAction ? 'View and load factor data update' : 'View factor data status';
    }, [loadInProgress, showLoadAction, summary?.loading]);

    if (!summary?.visible) {
        return null;
    }

    const setDetailsOpen = (nextOpen) => {
        if (isDetailsControlled) {
            onDetailsOpenChange(nextOpen);
            return;
        }
        setInternalDetailsOpen(nextOpen);
    };

    const handleLoad = async () => {
        try {
            await onLoadClick?.();
        } catch (error) {
            console.error('Error loading factor data update:', error);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    px: 0.75,
                    py: 0.5,
                    borderRadius: theme => resolveMsiSxRadius(
                        theme,
                        MSI_GEOMETRY.radii.button,
                        2,
                    ),
                    border: theme => `1px solid ${
                        usesAppStyleGrammar(theme) ? MSI_PALETTE.line : alpha(theme.palette.divider, 0.9)
                    }`,
                    backgroundColor: theme => (
                        usesAppStyleGrammar(theme)
                            ? MSI_PALETTE.surface
                            : alpha(theme.palette.background.paper, 0.92)
                    ),
                    minWidth: 0,
                }}
            >
                <Chip
                    size="small"
                    variant="outlined"
                    icon={<StorageRoundedIcon sx={{ fontSize: 16 }} />}
                    label="Factor Data"
                    sx={{ maxWidth: '100%' }}
                />
                <Chip
                    size="small"
                    color={summary.loading ? 'info' : summary.statusColor}
                    label={summary.loading ? 'Loading' : summary.freshnessLabel}
                />
                <Tooltip title={detailsTooltip}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => setDetailsOpen(true)}
                            aria-label="View factor data status"
                            sx={{
                                borderRadius: theme => resolveMsiSxRadius(
                                    theme,
                                    MSI_GEOMETRY.radii.iconButton,
                                    1.5,
                                ),
                                border: theme => `1px solid ${
                                    usesAppStyleGrammar(theme) ? MSI_PALETTE.line : alpha(theme.palette.divider, 0.9)
                                }`,
                            }}
                        >
                            {loadInProgress ? (
                                <CircularProgress size={16} />
                            ) : (
                                <AutorenewRoundedIcon sx={{ fontSize: 18 }} />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Dialog
                open={resolvedDetailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{summary.statusLabel || 'Factor Data Status'}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                        <Chip
                            size="small"
                            color={summary.loading ? 'info' : summary.statusColor}
                            label={summary.loading ? 'Loading' : summary.freshnessLabel}
                        />
                        {(summary.metadataChips || []).map((chip) => (
                            <Chip
                                key={chip.label}
                                size="small"
                                variant={chip.variant ?? 'outlined'}
                                color={chip.color ?? 'default'}
                                label={chip.label}
                            />
                        ))}
                    </Box>
                    {summary.loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2">
                                {summary.detailText || 'Loading factor data status...'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {summary.detailText ? (
                                <Typography variant="body2" sx={{ mb: 1.25 }}>
                                    {summary.detailText}
                                </Typography>
                            ) : null}
                            {summary.acceptedContextSummary ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Current context: {summary.acceptedContextSummary}
                                </Typography>
                            ) : null}
                            {summary.pendingContextSummary ? (
                                <Typography variant="body2" color="text.secondary">
                                    Available context: {summary.pendingContextSummary}
                                </Typography>
                            ) : null}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {showLoadAction ? (
                        <Button
                            variant="contained"
                            onClick={handleLoad}
                            disabled={loadDisabled || loadInProgress || summary.loading}
                            startIcon={loadInProgress ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {loadInProgress ? 'Loading...' : 'Load New Factor Data'}
                        </Button>
                    ) : null}
                    {summary.showRemindAction ? (
                        <Button onClick={() => setDetailsOpen(false)}>Remind Me Later</Button>
                    ) : (
                        <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

FactorDataStatusControl.propTypes = {
    summary: PropTypes.shape({
        visible: PropTypes.bool,
        loading: PropTypes.bool,
        isStale: PropTypes.bool,
        statusColor: PropTypes.string,
        statusLabel: PropTypes.string,
        freshnessLabel: PropTypes.string,
        detailText: PropTypes.string,
        acceptedContextSummary: PropTypes.string,
        pendingContextSummary: PropTypes.string,
        showLoadAction: PropTypes.bool,
        showRemindAction: PropTypes.bool,
        metadataChips: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string.isRequired,
            color: PropTypes.string,
            variant: PropTypes.string,
        })),
    }),
    onLoadClick: PropTypes.func,
    loadDisabled: PropTypes.bool,
    loadInProgress: PropTypes.bool,
    detailsOpen: PropTypes.bool,
    onDetailsOpenChange: PropTypes.func,
};

export default FactorDataStatusControl;
